import { useEffect, useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { IoMdClose } from 'react-icons/io'
import ResponsesSection from './ResponsesSection'
import useFeedbackActions from '../hooks/useFeedbackActions'
import { useFeedbacksContext } from '../context/feedbacks/FeedbacksProvider'
import { toastError, toastSuccess } from '../../../config/global'

const listVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
}

const HIDDEN_POPUP_RESPONSE_IDS = new Set([25])

export default function FeedbackPopupCard({ isOpen, onClose, onSubmitted }) {
  const { lng } = useParams()
  const { t } = useTranslation()
  const { feedbacksState } = useFeedbacksContext()
  const { getQuestions, sendAnswer, inProgress } = useFeedbackActions()
  const [answer, setAnswer] = useState({ feedback: '', responses: [] })
  const dir = lng === 'ar' ? 'rtl' : 'ltr'
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    getQuestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleSubmit() {
    try {
      const message = await sendAnswer(answer)
      if (message) {
        toastSuccess(message)
        onSubmitted()
      }
    } catch (error) {
      if (!error.response && !error.request) {
        toastError('لم يتم الاتصال بالخادم. يرجى التحقق من الاتصال بالإنترنت.')
      } else if (error.response) {
        toastError(error.response.data.error)
      } else if (error.request) {
        toastError('لم يتم استلام استجابة من الخادم. حاول مرة أخرى لاحقًا.')
      } else {
        toastError('حدث خطأ غير متوقع. حاول مرة أخرى لاحقًا.')
      }
    }
  }

  const spring = { type: 'spring', stiffness: 300, damping: 28 }
  const panelInitial = prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.96 }
  const panelAnimate = prefersReducedMotion ? { opacity: 1, transition: { duration: 0.2 } } : { opacity: 1, y: 0, scale: 1, transition: spring }
  const panelExit = prefersReducedMotion ? { opacity: 0, transition: { duration: 0.15 } } : { opacity: 0, y: 24, scale: 0.97, transition: { duration: 0.2 } }

  return (
    <Dialog
      static
      open={isOpen}
      onClose={onClose}
      className="relative z-50"
    >
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 bg-dark/20"
              aria-hidden="true"
            />
            <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center sm:inset-x-auto sm:bottom-6 sm:left-6 sm:justify-start">
              <DialogPanel
                as={motion.div}
                key="panel"
                initial={panelInitial}
                animate={panelAnimate}
                exit={panelExit}
                dir={dir}
                className="feedback-popup-card flex max-h-[85vh] w-full flex-col overflow-hidden border-2 border-muted-300 bg-white shadow-2xl sm:w-[400px]"
              >
                <span className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-300 sm:hidden" />

                <div className="relative h-24 w-full shrink-0 overflow-hidden">
                  <img
                    src="/assets/rev.png"
                    alt=""
                    className="h-full w-full object-cover object-center"
                  />
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('close')}
                    className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center border border-white/40 bg-white/20 text-white backdrop-blur transition hover:bg-white/40"
                  >
                    <IoMdClose size={18} />
                  </button>
                </div>

                <div className="flex flex-col gap-4 overflow-y-auto p-4">
                  <p className="text-sm text-muted-700">{t('FEEDBACKS_FIRST_MESSAGE')}</p>

                  {feedbacksState.isLoading ? (
                    <div className="flex animate-pulse flex-col gap-3">
                      <div className="h-20 border-2 border-muted-300 bg-muted-100" />
                      <div className="h-20 border-2 border-muted-300 bg-muted-100" />
                    </div>
                  ) : (
                    <motion.div
                      variants={listVariants}
                      initial="hidden"
                      animate="show"
                      className="flex flex-col gap-4"
                    >
                      {feedbacksState.questions?.map((question, index) => (
                        <motion.div
                          key={index}
                          variants={itemVariants}
                        >
                          <ResponsesSection
                            setAnswer={setAnswer}
                            question={{
                              ...question,
                              responses: question.responses?.filter(
                                (response) => !HIDDEN_POPUP_RESPONSE_IDS.has(response.id),
                              ),
                            }}
                          />
                        </motion.div>
                      ))}
                      <p className="text-sm text-muted-700">{t('thank_you_message')}</p>
                    </motion.div>
                  )}
                </div>

                <div className="shrink-0 border-t-2 border-muted-300 p-4">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={inProgress}
                    className="w-full bg-primary-600 py-3 font-bold text-white transition hover:bg-primary-700 disabled:bg-muted-600"
                  >
                    {inProgress ? t('inprogress') : t('send')}
                  </button>
                </div>
              </DialogPanel>
            </div>
          </>
        )}
      </AnimatePresence>
    </Dialog>
  )
}
